import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { api, getUser } from "../lib/api";
import { openExternal } from "../lib/native";
import { toast } from "sonner";
import { Banknote, ShieldCheck, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";

/**
 * StripeConnectCard — gates carrier bidding behind Stripe Connect Express
 * onboarding. Shown on the Dashboard for carriers. Required before any
 * bids can be submitted (server enforces too).
 */
export default function StripeConnectCard({ onChanged }) {
  const user = getUser();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/connect/account/status`, { params: { user_id: user.id } });
      setStatus(data);
    } catch (e) {
      // ignore — server may be offline
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "carrier") refresh();
    // eslint-disable-next-line
  }, []);

  if (!user || user.role !== "carrier") return null;

  const startOnboarding = async () => {
    setWorking(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post(
        `/connect/account/start`,
        { refresh_url: `${origin}/dashboard?connect=refresh`, return_url: `${origin}/dashboard?connect=return` },
        { params: { user_id: user.id } }
      );
      // F-02: in-app Safari View so the carrier never leaves the Binshare app.
      await openExternal(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start Stripe onboarding");
      setWorking(false);
    }
  };

  const openDashboard = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(
        `/connect/account/dashboard-link`,
        { return_url: `${window.location.origin}/dashboard` },
        { params: { user_id: user.id } }
      );
      // F-02: same in-app browser sheet for the Stripe Express dashboard.
      await openExternal(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open Stripe dashboard");
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="stripe-connect-card-loading" className="p-5 rounded-2xl border-[#E2DFD8]">
        <div className="flex items-center gap-2 text-sm text-[#6B6965]">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking payout setup…
        </div>
      </Card>
    );
  }

  // Fully ready — small confirmation card
  if (status?.charges_enabled && status?.details_submitted) {
    return (
      <Card data-testid="stripe-connect-card-ready" className="p-5 rounded-2xl border-[#4A6B56]/30 bg-[#4A6B56]/10 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#4A6B56] text-white flex items-center justify-center">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>Payouts active</div>
          <div className="text-xs text-[#4A6B56]">Stripe Connect linked · auto-paid on job completion</div>
        </div>
        <Button
          data-testid="stripe-dashboard-btn"
          variant="outline"
          onClick={openDashboard}
          disabled={working}
          className="rounded-full border-[#4A6B56] text-[#1B3624] hover:bg-white min-h-[40px]"
        >
          <ExternalLink className="w-4 h-4 mr-1" /> Stripe dashboard
        </Button>
      </Card>
    );
  }

  // Has account but onboarding incomplete
  if (status?.has_account) {
    return (
      <Card data-testid="stripe-connect-card-incomplete" className="p-5 rounded-2xl border-[#D67B54]/40 bg-[#FDF2EB]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D67B54] text-white flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>Finish payout setup</div>
            <div className="text-xs text-[#7A4A2A]">Stripe needs more details before you can accept paid jobs.</div>
          </div>
        </div>
        {status?.requirements_due?.length > 0 && (
          <ul className="mt-3 text-xs text-[#7A4A2A] list-disc list-inside space-y-0.5">
            {status.requirements_due.slice(0, 6).map((r) => <li key={r}>{r.replace(/[_.]/g, " ")}</li>)}
          </ul>
        )}
        <Button
          data-testid="stripe-continue-btn"
          onClick={startOnboarding}
          disabled={working}
          className="mt-4 rounded-full bg-[#D67B54] hover:bg-[#c66b44] text-white min-h-[44px]"
        >
          {working ? "Opening Stripe…" : "Continue Stripe onboarding"}
        </Button>
      </Card>
    );
  }

  // No account yet
  return (
    <Card data-testid="stripe-connect-card-start" className="p-5 rounded-2xl border-[#D67B54]/40 bg-[#FDF2EB]">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#1B3624] text-white flex items-center justify-center">
          <Banknote className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-medium text-[#1B3624]" style={{ fontFamily: "Outfit" }}>Connect your payouts</div>
          <p className="text-xs text-[#7A4A2A] mt-1">
            You need a Stripe Connect Express account to receive payments. We deduct Binshare's 5% fee and Stripe pays the rest straight to you. <strong>Required before bidding.</strong>
          </p>
        </div>
      </div>
      <Button
        data-testid="stripe-connect-btn"
        onClick={startOnboarding}
        disabled={working}
        className="mt-4 rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white min-h-[44px]"
      >
        {working ? "Opening Stripe…" : "Set up Stripe payouts"}
      </Button>
    </Card>
  );
}
